import { Button, Loading } from '@/components'
import { Layout } from '@/components'
import { DialogPopup } from '@/components/Dialog/DialogPopup'
import Input from '@/components/Form/Input'
import Password from '@/components/Form/Password'
import { availableState } from '@/features/authSlice'
import { dialogRegisterQuestionnaireState } from '@/features/dialogSlice'
import { raundUrl } from '@/lib/custom'
import { useCampaignHeaders } from '@/lib/useCampaignHeaders'
import { useCampaignSlug } from '@/lib/useCampaignSlug'
import { useFetchGet } from '@/lib/useFetch'
import { useI18nUrls } from '@/lib/useI18nUrls'
import { useIsIOS } from '@/lib/useIsIos'
import { useIsPWA } from '@/lib/useIsPwa'
import { useTracking } from '@/lib/useTracking'
import type { RootState } from '@/store/store'
import styles from '@/styles/Checkbox.module.scss'
import Head from 'next/head'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import type { SubmitHandler } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import useSWR from 'swr'
export { getStaticPaths, getStaticProps } from '@/lib/getStaticFunctions'

type Inputs = {
  username: string
  postalcode: string
  address: string
  password: string
  account: string
  passwordReInput?: string
  mail?: string
}

const Register = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { headers } = useCampaignHeaders()
  const router = useRouter()
  const { termsUrl, privacyPolicyUrl } = useI18nUrls()
  useTracking({ page: 'account' })
  const { gen, LOCAL_STORAGE_JWT_KEY, LOCAL_STORAGE_USERNAME_KEY, LOCAL_STORAGE_REFRESH_KEY } = useCampaignSlug()
  const { isPWA } = useIsPWA()
  const { isIOS } = useIsIOS()

  const [registerState, setRegisterState] = useState<string | undefined>('')
  const [acceptTermsAndPrivacyPolicy, setAcceptTermsAndPrivacyPolicy] = useState<boolean | undefined>(undefined)
  const availableStateSelector = useSelector((state: RootState) => state.auth.available)
  const loginTypeStateSelector = useSelector((state: RootState) => state.auth.loginType)

  const { getWithoutAuth: campaignGet } = useFetchGet<{ campaign: Api.CampaignResBody }>({})
  const { data: campaignData } = useSWR<{ campaign: Api.CampaignResBody }>(raundUrl.campaign, campaignGet)

  const useFormMethods = useForm<Inputs>()

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    formState: { errors },
  } = useFormMethods

  useEffect(() => {
    if (localStorage.getItem(LOCAL_STORAGE_JWT_KEY)) {
      router.push(gen('/auth/signin/'))
    }
    return
  }, [])

  useEffect(() => {
    if (campaignData?.campaign) {
      if (!campaignData.campaign.registration) {
        router.push(gen('/auth/signin/'))
      }
    }
    return
  }, [campaignData])

  const onSubmitAuthRegister: SubmitHandler<Inputs> = async (data) => {
    dispatch(availableState(true))

    try {
      /***
       * アカウント登録(バックエンドで登録)
       */
      const cognitoRegisterFunc = await fetch(raundUrl.register, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          username: data.account,
          password: data.password,
          password_confirm: data.passwordReInput,
        }),
      })

      const result = await cognitoRegisterFunc.json()

      if (result.status === 200) {
        localStorage.setItem(LOCAL_STORAGE_JWT_KEY, `${result.id_token}`)
        localStorage.setItem(LOCAL_STORAGE_REFRESH_KEY, `${result.refresh_token}`)
        localStorage.setItem(LOCAL_STORAGE_USERNAME_KEY, `${result.username}`)

        setRegisterState('')
        dispatch(availableState(true))
        dispatch(dialogRegisterQuestionnaireState(true))

        router.push(gen('/'))
        return
      }

      if (result.status === 400) {
        setRegisterState(result.message)
        dispatch(availableState(false))
        return
      }
    } catch (error) {
      setRegisterState(t('register.account_already_exists'))
      dispatch(availableState(false))
    }
  }

  return (
    <Layout title="">
      <Head>
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1.0,minimum-scale=1.0,maximum-scale=1.0,user-scalable=no"
        />
      </Head>

      <section className="w-full">
        <img src={campaignData?.campaign?.image} className="w-full" alt="" />
        <div className="mx-2 mt-10">
          {campaignData?.campaign?.isLineLogin && !(isIOS && isPWA) && (
            <>
              <div className="mx-auto flex w-11/12 items-center justify-center">
                <div className="h-[0.5px] w-[80%] bg-[#666]" />
                <span className="mx-[6px] whitespace-nowrap text-center text-[10px] font-light text-[#666]">
                  {t('register.register_with_line')}
                </span>
                <div className="h-[0.5px] w-[80%] bg-[#666]" />
              </div>
              <div className="text-center">
                <a
                  href={`${process.env.NEXT_PUBLIC_COGNITO_ENDPOINT}/oauth2/authorize?identity_provider=${campaignData?.campaign.cognitoIdentityProvider}&redirect_uri=${campaignData?.campaign.url}/auth/check/&response_type=CODE&client_id=${campaignData?.campaign.cognitoClientId}&scope=email openid profile`}
                  className="mx-auto mt-[20px] mb-[16px] block w-[71.7%]"
                >
                  <img src={`${process.env.NEXT_PUBLIC_SITEURL}/images/btn_line.png`} alt="" />
                </a>
              </div>
            </>
          )}
          <div className="mx-auto mt-[24px] flex w-11/12 items-center justify-center">
            <div className="h-[0.5px] w-[80%] bg-[#666]" />
            <span className="mx-[6px] whitespace-nowrap text-center text-[10px] font-light text-[#666]">
              {campaignData?.campaign?.isLineLogin && !(isIOS && isPWA)
                ? t('register.or_create_an_account')
                : t('register.create_an_account')}
            </span>
            <div className="h-[0.5px] w-[80%] bg-[#666]" />
          </div>

          <div className="mx-auto mt-5 w-11/12 rounded-lg px-6 py-7 shadow-primary">
            {registerState !== '' ? <div className="questerror text-sm">{`${registerState}`}</div> : <></>}
            <FormProvider {...useFormMethods}>
              <form onSubmit={handleSubmit(onSubmitAuthRegister)}>
                <fieldset>
                  {loginTypeStateSelector === 1 ? (
                    <div className="relative">
                      <Input
                        labelName={t('register.account_name')}
                        htmlfor="account"
                        type="text"
                        placeholder=""
                        register={register}
                        required
                        require={true}
                      />
                      <p className="text-xs text-color01">{t('register.account_name_notification')}</p>
                      {errors.account?.type === 'required' && (
                        <p className="error text-xss text-color01">
                          {t('register.account_name_notification_input_required')}
                        </p>
                      )}
                    </div>
                  ) : (
                    ''
                  )}

                  {loginTypeStateSelector === 2 ? (
                    <div className="relative">
                      <Input
                        labelName={t('register.email')}
                        htmlfor="account"
                        type="text"
                        placeholder=""
                        register={register}
                        validate={{
                          m: (v) =>
                            (v && /^[A-Za-z0-9]{1}[A-Za-z0-9_.-]*@{1}[A-Za-z0-9_.-]{1,}\.[A-Za-z0-9]{1,}$/.test(v)) ||
                            t('register.email_error_message_format_error'),
                        }}
                        required
                        require={true}
                      />
                      {errors.account?.type === 'm' && (
                        <p className="error text-xs text-color01">{errors.account?.message}</p>
                      )}
                      {errors.account?.type === 'required' && (
                        <p className="error text-xss text-color01">
                          {t('register.account_name_error_message_input_required')}
                        </p>
                      )}
                    </div>
                  ) : (
                    ''
                  )}

                  <div className="mt-6">
                    <Password
                      minLength={campaignData?.campaign.passwordCharacters}
                      validate={{
                        p: (v) => (v && /[0-9]/.test(v)) || t('register.password_error_message_number_required'),
                        e: (v) => (v && /[a-z]/.test(v)) || t('register.password_error_message_alphabet_required'),
                      }}
                    />
                  </div>

                  <div className="mt-6">
                    <Password
                      id="passwordReInput"
                      label={t('register.password_confirmation')}
                      validate={{
                        confirm: (v) =>
                          v === getValues('password') || t('register.password_confirmation_error_message_not_matched'),
                      }}
                    />
                  </div>
                </fieldset>

                <>
                  <div className={'flex items-center text-sm mt-4'}>
                    <label
                      className={'flex w-full cursor-pointer items-center whitespace-pre-wrap'}
                      htmlFor={'accept-terms-and-privacy-policy'}
                    >
                      <input
                        className={`${styles.c_checkbox_input}`}
                        type="checkbox"
                        id={'accept-terms-and-privacy-policy'}
                        checked={acceptTermsAndPrivacyPolicy || false}
                        onChange={(e) => setAcceptTermsAndPrivacyPolicy(e.target.checked)}
                      />
                      <span className={`${styles.c_checkbox_dummyInput}`}></span>
                      <div className={'w-full ml-5'}>
                        <Trans
                          i18nKey="register.accept_terms_and_privacy_policy"
                          className={'whitespace-nowrap'}
                          components={{
                            link1: (
                              <a
                                href={termsUrl}
                                className={'text-blue-500 underline whitespace-nowrap whitespace-pre-wrap'}
                                target="_new"
                                rel="noopener"
                              />
                            ),
                            link2: (
                              <a
                                href={privacyPolicyUrl}
                                className={'text-blue-500 underline whitespace-nowrap whitespace-pre-wrap'}
                                target="_new"
                                rel="noopener"
                              />
                            ),
                          }}
                          values={{
                            terms: t('register.terms'),
                            privacy_policy: t('register.privacy_policy'),
                          }}
                        />
                      </div>
                    </label>
                  </div>
                </>
                {acceptTermsAndPrivacyPolicy}
                <p className="mt-10 text-center text-sm font-bold">{t('register.register_information_notification')}</p>
                <Button
                  text={t('register.button_create_an_account')}
                  type="submit"
                  classes="mt-6"
                  disabled={!acceptTermsAndPrivacyPolicy}
                />

                <Button
                  text={t('register.button_if_you_have_account')}
                  borderColor="border-main"
                  bgColor="bg-[#fff]"
                  classes="mt-6"
                  href={gen('/auth/signin/')}
                />
              </form>
            </FormProvider>
          </div>
        </div>
      </section>

      {availableStateSelector ? <Loading /> : ''}

      <DialogPopup hookpoint="REGISTER" campaign={campaignData?.campaign} />
    </Layout>
  )
}

export default Register
